// Static data, constants, derived stats, and helpers extracted from EchoFrontDesk.js
// NO behavior changes — pure data module.
/* DEMO_FIXTURES_ONLY */

import {
  Calendar, DoorOpen, Stethoscope, ClipboardCheck,
} from 'lucide-react';
import { useAppMode } from '../../../contexts/AppModeContext';

/* ═══ COLORS ═══ */
export const C = { green: '#22c55e', greenBtn: '#16a34a', amber: '#f59e0b', red: '#ef4444', purple: '#6b5ce7', black: '#111' };

/* ═══ PIPELINE (4 stages) ═══ */
export const STAGES = [
  { key: 'scheduled', label: 'Scheduled', icon: Calendar },
  { key: 'arrived', label: 'Arrived', icon: DoorOpen },
  { key: 'with_provider', label: 'With Provider', icon: Stethoscope },
  { key: 'checked_out', label: 'Checked Out', icon: ClipboardCheck },
];

/* ═══ MOCK DATA ═══ */
export const DEMO_PATIENTS = [
  {
    id:'P001', name:'John Smith', dob:'03/15/1962', sex:'M', mrn:'MRN-001247',
    time:'9:00 AM', type:'Follow-up', provider:'Dr. Chen', stage:'arrived', room:'Room 1',
    fhirConnected:true, arrivedAt:'8:58 AM', waitMin:12,
    copay:{amount:25,collected:true,payer:'Blue Cross'},
    noShowRisk:0,
    insurance:{payer:'Blue Cross',memberId:'BC-88234561',group:'GRP-4421',copay:'$25',status:'Active',verified:true,verifiedAt:'8:45 AM'},
    demographics:{phone:'(555) 123-4567',email:'jsmith@email.com',address:'123 Oak St, LA 90024',emergencyContact:'Mary Smith — (555) 234-5678'},
    allergies:['Penicillin','Sulfa drugs'],
    vitals:{bp:'128/82',hr:'72 bpm',temp:'98.6 F',weight:'185 lbs'},
    aiStatus:{type:'confirmed',text:'AI confirmed · 8:45 AM',color:C.green},
    aiActions:[
      {priority:'green',title:'Insurance verified',detail:'Blue Cross active through 12/2026',financial:'$25 copay collected at check-in',done:true},
      {priority:'green',title:'AI confirmed appointment',detail:'Phone call confirmed at 8:45 AM',financial:'',done:true},
      {priority:'green',title:'Intake complete',detail:'All forms submitted prior to arrival',financial:'',done:true},
    ],
    comms:[
      {type:'ai',text:'Appointment confirmed via AI call',time:'8:45 AM'},
      {type:'system',text:'Checked in at front desk',time:'8:58 AM'},
    ],
    nextAppts:[{date:'Apr 15',time:'10:00 AM',type:'Follow-up',provider:'Dr. Chen'}],
  },
  {
    id:'P002', name:'Maria Garcia', dob:'07/22/1988', sex:'F', mrn:'MRN-001352',
    time:'9:30 AM', type:'New Patient', provider:'Dr. Wilson', stage:'with_provider', room:'Room 2',
    fhirConnected:true, arrivedAt:'9:28 AM', waitMin:0,
    copay:{amount:30,collected:true,payer:'Aetna'},
    noShowRisk:0,
    insurance:{payer:'Aetna',memberId:'AET-3345678',group:'GRP-1129',copay:'$30',status:'Active',verified:true,verifiedAt:'9:20 AM'},
    demographics:{phone:'(555) 345-6789',email:'mgarcia@email.com',address:'456 Elm Ave, LA 90025',emergencyContact:'Carlos Garcia — (555) 456-7890'},
    allergies:[],
    vitals:{bp:'118/76',hr:'68 bpm',temp:'98.4 F',weight:'142 lbs'},
    aiStatus:{type:'confirmed',text:'AI confirmed · 8:15 AM',color:C.green},
    aiActions:[
      {priority:'green',title:'Insurance verified',detail:'Aetna active, copay $30 collected',financial:'$30 copay collected',done:true},
      {priority:'green',title:'New patient intake complete',detail:'Online forms submitted',financial:'',done:true},
      {priority:'blue',title:'Annual Wellness Visit due',detail:'Fully covered, $0 patient cost',financial:'$250 reimbursement to practice',done:false},
    ],
    comms:[
      {type:'ai',text:'Reminder sent via SMS',time:'7:30 AM'},
      {type:'ai',text:'Appointment confirmed via AI call',time:'8:15 AM'},
      {type:'system',text:'Checked in, roomed',time:'9:28 AM'},
    ],
    nextAppts:[],
  },
  {
    id:'P003', name:'Robert Johnson', dob:'11/08/1975', sex:'M', mrn:'MRN-001488',
    time:'10:00 AM', type:'Sick Visit', provider:'Dr. Chen', stage:'arrived', room:null,
    fhirConnected:false, arrivedAt:'9:55 AM', waitMin:8,
    copay:{amount:null,collected:false,payer:null},
    noShowRisk:0,
    insurance:null,
    demographics:{phone:'(555) 567-8901',email:null,address:null,emergencyContact:null},
    allergies:null, vitals:null,
    aiStatus:{type:'insurance_flagged',text:'Insurance not on file',color:C.red},
    aiActions:[
      {priority:'red',title:'No insurance on file',detail:'Cannot bill without active insurance',financial:'Submitting claim will result in denial',done:false},
      {priority:'red',title:'Missing demographics',detail:'Scan ID to populate patient record',financial:'',done:false},
      {priority:'amber',title:'Copay unknown',detail:'Determine copay after insurance scan',financial:'Collection risk increases after visit',done:false},
    ],
    comms:[{type:'ai',text:'Reminder sent, no response',time:'8:00 AM'}],
    nextAppts:[],
  },
  {
    id:'P004', name:'Emily Davis', dob:'05/30/1990', sex:'F', mrn:'MRN-001521',
    time:'10:30 AM', type:'Follow-up', provider:'Dr. Chen', stage:'scheduled', room:null,
    fhirConnected:true, arrivedAt:null, waitMin:0,
    copay:{amount:20,collected:false,payer:'UnitedHealth'},
    noShowRisk:5,
    insurance:{payer:'UnitedHealth',memberId:'UHC-7712345',group:'GRP-8803',copay:'$20',status:'Active',verified:true,verifiedAt:'Yesterday'},
    demographics:{phone:'(555) 678-9012',email:'edavis@email.com',address:'789 Pine Rd, LA 90026',emergencyContact:'Tom Davis — (555) 789-0123'},
    allergies:['Latex'], vitals:null,
    aiStatus:{type:'confirmed',text:'AI confirmed via text · 8:30 AM',color:C.green},
    aiActions:[
      {priority:'amber',title:'Collect $20 copay',detail:'UnitedHealth requires upfront collection',financial:'$20 copay — risk of non-collection post-visit',done:false},
      {priority:'green',title:'AI confirmed appointment',detail:'Confirmed via text at 8:30 AM',financial:'',done:true},
      {priority:'green',title:'Insurance verified',detail:'UnitedHealth active through 06/2026',financial:'',done:true},
    ],
    comms:[{type:'ai',text:'Appointment confirmed via text',time:'8:30 AM'}],
    nextAppts:[{date:'May 1',time:'2:00 PM',type:'Wellness',provider:'Dr. Chen'}],
  },
  {
    id:'P005', name:'James Taylor', dob:'01/14/1968', sex:'M', mrn:'MRN-001603',
    time:'11:00 AM', type:'Wellness', provider:'Dr. Wilson', stage:'scheduled', room:null,
    fhirConnected:false, arrivedAt:null, waitMin:0,
    copay:{amount:null,collected:false,payer:null},
    noShowRisk:35,
    insurance:null,
    demographics:{phone:'(555) 890-1234',email:null,address:null,emergencyContact:null},
    allergies:null, vitals:null,
    aiStatus:{type:'no_show_risk',text:'35% no-show risk · no response',color:C.amber},
    aiActions:[
      {priority:'red',title:'High no-show risk (35%)',detail:'Patient has not confirmed appointment',financial:'Each no-show = ~$200 lost revenue',done:false},
      {priority:'red',title:'No insurance or ID on file',detail:'Full intake required at arrival',financial:'Cannot determine copay without insurance',done:false},
      {priority:'amber',title:'AI sent 2 reminders — no response',detail:'Last attempt at 9:30 AM',financial:'',done:false},
    ],
    comms:[
      {type:'ai',text:'Reminder sent, no response',time:'8:00 AM'},
      {type:'ai',text:'2nd reminder — no answer',time:'9:30 AM'},
    ],
    nextAppts:[],
  },
  {
    id:'P006', name:'Sarah Williams', dob:'09/03/1982', sex:'F', mrn:'MRN-001155',
    time:'1:00 PM', type:'Follow-up', provider:'Dr. Chen', stage:'scheduled', room:null,
    fhirConnected:true, arrivedAt:null, waitMin:0,
    copay:{amount:25,collected:false,payer:'Cigna'},
    noShowRisk:8,
    insurance:{payer:'Cigna',memberId:'CIG-4456789',group:'GRP-2204',copay:'$25',status:'Active',verified:true,verifiedAt:'Yesterday'},
    demographics:{phone:'(555) 901-2345',email:'swilliams@email.com',address:'321 Maple Dr, LA 90027',emergencyContact:'Jim Williams — (555) 012-3456'},
    allergies:['Aspirin'], vitals:null,
    aiStatus:{type:'confirmed',text:'AI confirmed via SMS · 7:30 AM',color:C.green},
    aiActions:[
      {priority:'amber',title:'Collect $25 copay at check-in',detail:'Cigna requires upfront collection',financial:'$25 copay due',done:false},
      {priority:'green',title:'All pre-visit requirements met',detail:'Insurance verified, intake complete',financial:'',done:true},
    ],
    comms:[{type:'ai',text:'Confirmed via SMS',time:'7:30 AM'}],
    nextAppts:[],
  },
  {
    id:'P007', name:'David Wilson', dob:'12/19/1955', sex:'M', mrn:'MRN-000982',
    time:'2:00 PM', type:'New Patient', provider:'Dr. Wilson', stage:'scheduled', room:null,
    fhirConnected:false, arrivedAt:null, waitMin:0,
    copay:{amount:null,collected:false,payer:null},
    noShowRisk:42,
    insurance:null,
    demographics:{phone:'(555) 234-5678',email:null,address:null,emergencyContact:null},
    allergies:null, vitals:null,
    aiStatus:{type:'no_show_risk',text:'42% no-show risk · no confirmation',color:C.red},
    aiActions:[
      {priority:'red',title:'High no-show risk (42%)',detail:'No response to any outreach attempts',financial:'Each no-show = ~$200 lost revenue',done:false},
      {priority:'red',title:'No records on file',detail:'New patient — full intake required',financial:'Cannot process billing without insurance',done:false},
    ],
    comms:[
      {type:'system',text:'Scheduled via online portal',time:'Yesterday'},
      {type:'ai',text:'Confirmation call — no answer',time:'8:00 AM'},
    ],
    nextAppts:[],
  },
  {
    id:'P008', name:'Lisa Brown', dob:'04/25/1978', sex:'F', mrn:'MRN-001089',
    time:'3:00 PM', type:'Sick Visit', provider:'Dr. Chen', stage:'checked_out', room:null,
    fhirConnected:true, arrivedAt:'2:45 PM', waitMin:0,
    copay:{amount:0,collected:true,payer:'Medicare'},
    noShowRisk:0,
    insurance:{payer:'Medicare',memberId:'MCA-1123456',group:'-',copay:'$0',status:'Active',verified:true,verifiedAt:'2:45 PM'},
    demographics:{phone:'(555) 345-6789',email:'lbrown@email.com',address:'654 Cedar Ln, LA 90028',emergencyContact:'Mike Brown — (555) 456-7890'},
    allergies:[], vitals:{bp:'130/85',hr:'78 bpm',temp:'99.1 F',weight:'165 lbs'},
    aiStatus:{type:'confirmed',text:'Visit complete',color:'#d1d5db'},
    aiActions:[
      {priority:'green',title:'Visit complete',detail:'Checked out at 2:45 PM',financial:'$0 copay (Medicare)',done:true},
      {priority:'green',title:'Follow-up scheduled',detail:'Apr 20 at 3:00 PM',financial:'',done:true},
    ],
    comms:[{type:'system',text:'Visit completed, checked out',time:'2:45 PM'}],
    nextAppts:[{date:'Apr 20',time:'3:00 PM',type:'Follow-up',provider:'Dr. Chen'}],
  },
];

export const DEMO_AI_FEED = [
  { color: C.green, text: 'Confirmed Maria Garcia', time: '9:28 AM', icon: '\u2713' },
  { color: C.green, text: 'Verified insurance \u2014 John Smith', time: '8:45 AM', icon: '\u2713' },
  { color: C.amber, text: 'Flagged 42% no-show risk \u2014 David Wilson', time: '9:35 AM', icon: '\u26a0' },
  { color: C.amber, text: 'Escalated: Robert Johnson \u2014 insurance question', time: '9:15 AM', icon: '\u26a0' },
  { color: '#6b5ce7', text: 'Sent intake forms \u2014 Emily Davis', time: '8:32 AM', icon: '\u2192' },
  { color: '#6b5ce7', text: 'Filled 11:30 slot from waitlist', time: '9:28 AM', icon: '\u26a1' },
  { color: C.green, text: 'Sent reminder \u2014 Sarah Williams', time: '7:30 AM', icon: '\u2713' },
  { color: C.red, text: 'Insurance inactive alert \u2014 James Taylor', time: '8:05 AM', icon: '\u2715' },
];

export const DEMO_ATTENTION_ITEMS = [
  { priority: 'red', patient: 'David Wilson', issue: 'High no-show risk (42%) \u2014 no confirmation', financial: 'Each no-show = ~$200 lost', action: 'Call', pid: 'P007' },
  { priority: 'red', patient: 'Robert Johnson', issue: 'No insurance on file \u2014 cannot bill', financial: 'Claim denial if unresolved', action: 'Scan ID', pid: 'P003' },
  { priority: 'amber', patient: 'James Taylor', issue: '35% no-show risk \u2014 2 reminders sent, no response', financial: '~$200 at risk', action: 'Call', pid: 'P005' },
  { priority: 'amber', patient: 'Emily Davis', issue: '$20 copay uncollected', financial: 'Collection rates drop 40% post-visit', action: 'Collect $20', pid: 'P004' },
  { priority: 'blue', patient: 'Maria Garcia', issue: 'Annual Wellness Visit due \u2014 fully covered', financial: '$250 reimbursement to practice', action: 'Flag provider', pid: 'P002' },
];

export const WEEK_DAYS = (() => {
  const today = new Date();
  const mon = new Date(today); mon.setDate(today.getDate() - today.getDay() + 1);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i);
    const isToday = d.toDateString() === today.toDateString();
    return {
      day: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i],
      date: d.getDate(),
      isToday,
      appointments: isToday ? 8 : [6,7,5,8,4,0,0][i],
      hasRisk: [false,true,false,false,false,false,false][i] || isToday,
      hasOpen: [true,false,true,false,false,false,false][i] || isToday,
    };
  });
})();

/* ═══ DERIVED STATS ═══ */
export const totalSlots = 10;
export const filledSlots = DEMO_PATIENTS.length;
export const openSlots = totalSlots - filledSlots;
export const copaysCollected = DEMO_PATIENTS.filter(p => p.copay.collected && p.copay.amount > 0).reduce((s, p) => s + p.copay.amount, 0);
export const copaysOutstanding = DEMO_PATIENTS.filter(p => !p.copay.collected && p.copay.amount > 0).reduce((s, p) => s + p.copay.amount, 0);
export const copaysPendingCount = DEMO_PATIENTS.filter(p => !p.copay.collected && p.copay.amount > 0).length;
export const noShowRiskCount = DEMO_PATIENTS.filter(p => p.noShowRisk >= 25).length;
export const revenueAtRisk = noShowRiskCount * 200;
export const estRevenue = 2840;
export const avgWait = Math.round(DEMO_PATIENTS.filter(p => p.stage === 'arrived' && p.waitMin > 0).reduce((s, p) => s + p.waitMin, 0) / Math.max(1, DEMO_PATIENTS.filter(p => p.stage === 'arrived').length));
export const aiActionsCount = 14;
export const checkedInCount = DEMO_PATIENTS.filter(p => p.stage === 'arrived' || p.stage === 'with_provider').length;
export const waitingCount = DEMO_PATIENTS.filter(p => p.stage === 'arrived').length;
export const today = new Date();
export const todayStr = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/* ═══ HELPER: priority icon color ═══ */
export const pColor = (p) => p === 'red' ? C.red : p === 'amber' ? C.amber : p === 'green' ? C.green : '#3b82f6';
export const pBg = (p) => p === 'red' ? '#fef2f2' : p === 'amber' ? '#fffbeb' : p === 'green' ? '#f0fdf4' : '#eff6ff';
